/**
 * Reusable language picker.
 *
 * Used by:
 *   - the auth screen (top of the login / sign-up panel)
 *   - the main menu (above the callsign / race form)
 *   - the pause menu's "General" pane (live in-game switching)
 *
 * Renders a styled `<select>` populated from {@link getAvailableLocales}
 * with friendly display names from {@link getLocaleDisplayName}. When
 * the user picks a value the selector calls {@link setLocale}, persists
 * it via {@link persistLocale}, then invokes the caller's `onChange`
 * callback so the surrounding UI can re-render. Keeping the persistence
 * inside the picker means every call site stays consistent — there is
 * no path to "the dropdown updated the language but the next page load
 * forgot it".
 */

import {
  setLocale,
  getLocale,
  getAvailableLocales,
  getLocaleDisplayName,
  type Locale,
} from '@viral-space/shared';
import { COLORS, FONT, el } from './ui-shared';
import { persistLocale } from './locale-storage';

export interface LanguageSelectorOptions {
  /** Called after the locale has been changed and persisted. */
  onChange?: (locale: Locale) => void;
  /**
   * When `true` (the default), the wrapper renders a small "LANGUAGE:"
   * caption to the left of the select. Pass `false` to embed the bare
   * `<select>` (e.g. inside a labelled row that already renders the
   * caption itself).
   */
  showLabel?: boolean;
  /** Override the caption text. Defaults to "LANGUAGE". */
  labelText?: string;
}

/**
 * Build a language-selector control. Returns the wrapper element so
 * the caller can append it wherever it likes.
 */
export function createLanguageSelector(opts: LanguageSelectorOptions = {}): HTMLElement {
  const showLabel = opts.showLabel !== false;

  const wrap = el('div', {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  });

  if (showLabel) {
    const label = el('span', {
      fontSize: '10px',
      color: COLORS.dim,
      letterSpacing: '2px',
      textTransform: 'uppercase',
    }, opts.labelText ?? 'LANGUAGE');
    wrap.appendChild(label);
  }

  const select = document.createElement('select');
  Object.assign(select.style, {
    fontFamily: FONT,
    fontSize: '12px',
    padding: '6px 12px',
    background: 'rgba(0,12,0,0.85)',
    color: COLORS.primary,
    border: `1px solid ${COLORS.primary}`,
    borderRadius: '0',
    cursor: 'pointer',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    pointerEvents: 'auto',
    outline: 'none',
    minWidth: '140px',
    // The native <select> arrow is hidden because most browsers paint
    // it in OS-native chrome that clashes with the green-on-black HUD.
    // The arrow is re-rendered as a CSS background instead.
    appearance: 'none',
    WebkitAppearance: 'none' as string,
    MozAppearance: 'none' as string,
    backgroundImage:
      `linear-gradient(45deg, transparent 50%, ${COLORS.primary} 50%),
       linear-gradient(135deg, ${COLORS.primary} 50%, transparent 50%)`,
    backgroundPosition:
      'calc(100% - 14px) calc(50% - 2px), calc(100% - 9px) calc(50% - 2px)',
    backgroundSize: '5px 5px, 5px 5px',
    backgroundRepeat: 'no-repeat',
    paddingRight: '28px',
  });

  const current = getLocale();
  for (const code of getAvailableLocales()) {
    const opt = document.createElement('option');
    opt.value = code;
    opt.textContent = getLocaleDisplayName(code);
    Object.assign(opt.style, {
      background: 'rgba(0,12,0,0.95)',
      color: COLORS.primary,
    });
    if (code === current) opt.selected = true;
    select.appendChild(opt);
  }

  select.addEventListener('change', () => {
    const next = select.value as Locale;
    if (!next || next === getLocale()) return;
    setLocale(next);
    persistLocale(next);
    opts.onChange?.(next);
  });

  wrap.appendChild(select);
  return wrap;
}
